import {Editor} from '@tiptap/react';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {collectEnumValues} from '../../../../../../shared/helpers/src/typescript/enumHelpers';
import {DropdownBoxCoordinator} from '../../../../../../shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../../../shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../../shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../../shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../../shared/ui/src/components/dropdowns/dropdownList';
import {PopoverTransferProps} from '../../../../../../shared/ui/src/components/popovers/popoverCoordinator';

/*
 * Constants.
 */

const defaultDropdownWidth = 114; // px.
enum HeadingsEnum {
  H1 = 1,
  H2 = 2,
  H3 = 3,
  PARAGRAPH = 4,
}

/*
 * Props.
 */

interface HeadingDropdownProps {
  editor: Editor;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  width: ${defaultDropdownWidth}px;
`;

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0px;
`;

/*
 * Component.
 */

export const HeadingDropdown: FC<HeadingDropdownProps> = ({editor}) => {
  const activeHeading = findActiveHeading(editor);
  return (
    <StyledWrapperDiv>
      <StyledDropdownBoxCoordinator
        value={renderLabel(activeHeading)}
        renderDropdown={(popoverProps) => renderDropdown(popoverProps, editor)}
      />
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderDropdown(popoverProps: PopoverTransferProps, editor: Editor) {
  const onSelect = (heading: HeadingsEnum) => {
    if (heading === HeadingsEnum.PARAGRAPH) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({level: heading}).run();
    }
  };
  return (
    <Dropdown {...popoverProps} width={defaultDropdownWidth}>
      <DropdownList {...popoverProps}>
        <DropdownItem onSelect={() => onSelect(HeadingsEnum.H1)}>
          <DropdownItemContent>{renderLabel(HeadingsEnum.H1)}</DropdownItemContent>
        </DropdownItem>
        <DropdownItem onSelect={() => onSelect(HeadingsEnum.H2)}>
          <DropdownItemContent>{renderLabel(HeadingsEnum.H2)}</DropdownItemContent>
        </DropdownItem>
        <DropdownItem onSelect={() => onSelect(HeadingsEnum.H3)}>
          <DropdownItemContent>{renderLabel(HeadingsEnum.H3)}</DropdownItemContent>
        </DropdownItem>
        <DropdownItem onSelect={() => onSelect(HeadingsEnum.PARAGRAPH)}>
          <DropdownItemContent>{renderLabel(HeadingsEnum.PARAGRAPH)}</DropdownItemContent>
        </DropdownItem>
      </DropdownList>
    </Dropdown>
  );
}

function renderLabel(heading?: HeadingsEnum) {
  switch (heading) {
    case HeadingsEnum.H1:
      return <Trans id="knowledge-base-tool-heading-h1">H1</Trans>;
    case HeadingsEnum.H2:
      return <Trans id="knowledge-base-tool-heading-h2">H2</Trans>;
    case HeadingsEnum.H3:
      return <Trans id="knowledge-base-tool-heading-h3">H3</Trans>;
    case HeadingsEnum.PARAGRAPH:
    default:
      return <Trans id="knowledge-base-tool-heading-paragraph">Paragraph</Trans>;
  }
}

function findActiveHeading(editor: Editor) {
  return collectEnumValues(HeadingsEnum).find((heading) => {
    if (HeadingsEnum.PARAGRAPH === heading) {
      return editor.isActive('paragraph');
    }
    return editor.isActive('heading', {level: heading});
  });
}
